"use client"

import { BluredOverlay } from "@/app/components/blured-overlay"
import { useIsSticky } from "@/app/hooks/use-sticky"
import { useBannerState } from "@/app/layout/banner/banner"
import type { CollectionId } from "@anaconda/core/nft-collection/types"
import { Trans } from "@lingui/react/macro"
import { NavigationTabsList, cn } from "@rarible_int/anaconda_ui_components"
import { CollectionPageType } from "../../navigation/collection/collection-page-type"
import { FloorPriceChartToggleReatom } from "../floor-price-chart/floor-price-chart-toggle.reatom"
import { UserNftsCounterBadgeReatom } from "../items/user-items/counter/badge.reatom"
import { EssentialCollectionStatistic } from "../statistic/essential-collection-statistic"
import { CollectionPageLink } from "./link"

interface Props {
  collectionId: CollectionId
  routes: CollectionPageType[]
}

const LINK_BY_PAGE_TYPE = {
  [CollectionPageType.ITEMS]: ItemsNavigationItem,
  [CollectionPageType.MY_ITEMS]: MyItemsNavigationItem,
  [CollectionPageType.OFFERS]: OffersNavigationItem,
  [CollectionPageType.ACTIVITY]: ActivityNavigationItem,
}

export function CollectionNavigationViewReatom(props: Props) {
  const { isSticky, ref } = useIsSticky()
  const { isBannerClosed } = useBannerState()

  return (
    <BluredOverlay
      ref={ref}
      className={cn(
        "px-4 pb-3 md:sticky md:z-40 md:h-[theme('spacing.collection-navigation-height')] md:px-8 md:pb-4",
        isBannerClosed
          ? "md:top-[theme('spacing.header-height')]"
          : "md:top-[theme('spacing.header-with-status-height')]",
      )}
    >
      <div className="flex flex-wrap items-center justify-between gap-4 border-b md:gap-6">
        <NavigationTabsList
          variant="tabs"
          className="flex-nowrap overflow-x-auto md:flex-wrap md:overflow-visible"
        >
          {props.routes.map((route) => {
            const Link = LINK_BY_PAGE_TYPE[route]

            return <Link key={route} collectionId={props.collectionId} />
          })}
        </NavigationTabsList>
        {isSticky ? (
          <EssentialCollectionStatistic />
        ) : (
          <FloorPriceChartToggleReatom />
        )}
      </div>
    </BluredOverlay>
  )
}

interface NavigationItemProps {
  collectionId: CollectionId
}

function ItemsNavigationItem({ collectionId }: NavigationItemProps) {
  return (
    <CollectionPageLink
      collectionId={collectionId}
      pageType={CollectionPageType.ITEMS}
    >
      <Trans>Items</Trans>
    </CollectionPageLink>
  )
}

function MyItemsNavigationItem({ collectionId }: NavigationItemProps) {
  return (
    <CollectionPageLink
      collectionId={collectionId}
      pageType={CollectionPageType.MY_ITEMS}
    >
      <div className="flex gap-1">
        <Trans>My Items</Trans>
        <UserNftsCounterBadgeReatom />
      </div>
    </CollectionPageLink>
  )
}

function OffersNavigationItem({ collectionId }: NavigationItemProps) {
  return (
    <CollectionPageLink
      collectionId={collectionId}
      pageType={CollectionPageType.OFFERS}
    >
      <Trans>Offers</Trans>
    </CollectionPageLink>
  )
}

function ActivityNavigationItem({ collectionId }: NavigationItemProps) {
  return (
    <CollectionPageLink
      collectionId={collectionId}
      pageType={CollectionPageType.ACTIVITY}
    >
      <Trans>Activity</Trans>
    </CollectionPageLink>
  )
}
